import { useDispatch } from "react-redux";
import Icon from "@gc-npm/components.icon-glyph";
import styles from "./style";
import Spinner from "@gc-npm/components.spin";
import getColor from "@gc-npm/utils.enums.colors";
import { useSelector } from "react-redux";
import { getFullUrl } from "@/utils/htmlUtils";
import { generateQueryString } from "@/utils/normalizers/product";
import { useRef, useState, useEffect, useId } from "react";
import { useRouter } from "next/router";
import AutocompleteHits from "../AutocompleteHits";
import { actions as searcherActions } from "@/reducers/searcher";

const CustomSearchBox = ({
    placeholder,
    ariaLabel,
    id,
    onSubmit,
    onFocus,
    onBlur
}) => {
    const {
        searcherQuery,
        selectedFilters,
        sort,
        id: idRoute,
    } = useSelector((state) => state.devicesList);
    const {
        hits,
        query: autocompleteQuery,
        autocompleteFetching: loading,
    } = useSelector((state) => state.searcher);
    const dispatch = useDispatch();

    const router = useRouter();
    const { query: routerQuery } = router;
    const [autocompleteFocusIndex, setAutocompleteFocusIndex] = useState(undefined);
    const [inputValue, setInputValue] = useState(searcherQuery || "");
    const [showHits, setShowHits] = useState(false);
    const inputRef = useRef();
    const resultsId = useId();
    const hasSuggestions = Boolean(autocompleteQuery && inputValue && hits.length);
    const expanded = showHits && hasSuggestions;
    const activeOptionId = expanded && autocompleteFocusIndex !== undefined && hits[autocompleteFocusIndex]
        ? `${resultsId}-option-${autocompleteFocusIndex}`
        : undefined;

    // reference to debounce timer so we can cancel previous call
    const debounceTimer = useRef(null);

    useEffect(() => {
        setInputValue(searcherQuery || "");
    }, [searcherQuery])

    useEffect(() => () => clearTimeout(debounceTimer.current), []);

    const handleChange = (event) => {
        const value = event.currentTarget.value;
        setInputValue(value);
        setAutocompleteFocusIndex(undefined);
        setShowHits(true);

        //Implement debounce
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            dispatch(
                searcherActions.initAutocompleteQuery({
                    id,
                    query: value,
                })
            );
        }, 300);
    };

    const ClearQuery = () => {
        clearTimeout(debounceTimer.current);
        setShowHits(false);
        setAutocompleteFocusIndex(undefined);
        const selectedFiltersNew = { ...selectedFilters };

        selectedFiltersNew.query = "";
        selectedFiltersNew.id = idRoute;
        const url = getFullUrl(`/${idRoute}?sort=${sort}&${generateQueryString(selectedFiltersNew)
            }`, routerQuery);
        router.push(url, undefined, { shallow: true });
        setInputValue("");
        inputRef.current?.focus();
    };

    const handleOnSubmit = ({ hit, inputValue }) => {
        let submitQuery = inputValue;
        if (hit) {
            const { name } = hit;
            setInputValue(name);
            submitQuery = name;
        }
        setShowHits(false);
        clearTimeout(debounceTimer.current);
        dispatch(
            searcherActions.initAutocompleteQuery({
                id,
                query: submitQuery,
            })
        );
        inputRef.current.blur();
        setAutocompleteFocusIndex(undefined);
        onSubmit(submitQuery)
    }

    const handleInputKeyDown = (event) => {
        let focusIndex = autocompleteFocusIndex !== undefined ? autocompleteFocusIndex : -1;

        if (event.key === 'Escape') {
            if (showHits) event.preventDefault();
            setShowHits(false);
            setAutocompleteFocusIndex(undefined);
        } else if (event.key === 'ArrowDown' && hasSuggestions) {
            event.preventDefault();
            focusIndex = (focusIndex + 1) % hits.length;
            setShowHits(true);
            setAutocompleteFocusIndex(focusIndex);
            setInputValue(hits[focusIndex].name);
        } else if (event.key === 'ArrowUp' && hasSuggestions) {
            event.preventDefault();
            focusIndex = focusIndex <= 0 ? hits.length - 1 : focusIndex - 1;
            setAutocompleteFocusIndex(focusIndex);
            setShowHits(true);
            setInputValue(hits[focusIndex].name);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (expanded && activeOptionId) {
                const hit = hits[focusIndex];
                handleOnSubmit({ hit });
            } else {
                handleOnSubmit({ inputValue: inputRef.current.value });
            }
        }
    }

    const handleFocus = async () => {
        setShowHits(true);
        onFocus();
    };

    const handleBlur = () => {
        setShowHits(false);
        setAutocompleteFocusIndex(undefined);
        onBlur();
    };

    return (
        <>
            <form
                className="searcher-box"
                onMouseDown={onFocus}
                onSubmit={(event) => {
                    event.preventDefault();
                    handleOnSubmit({ inputValue: inputRef.current.value });
                }}
            >
                <div className="search-icon-container" aria-hidden="true">
                    <Icon id="buscar-m" height={22} color="GREY_600" alt="" />
                </div>
                <input
                    className="searcher-input"
                    value={inputValue}
                    type="search"
                    name="buscador"
                    id={id}
                    aria-label={ariaLabel}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={expanded}
                    aria-controls={resultsId}
                    aria-activedescendant={activeOptionId}
                    onChange={handleChange}
                    placeholder={placeholder}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    autoComplete="off"
                    onKeyDownCapture={handleInputKeyDown}
                    onClick={() => setShowHits(true)}
                    ref={inputRef}
                />
                {false && loading && (
                    <div className="loading-container" aria-hidden="true">
                        <Spinner
                            operation="loading"
                            fillColor={getColor("GREY_600")}
                            strokeColor={getColor("GREY_600")}
                        />
                    </div>
                )}
                {searcherQuery !== "" && (
                    <button type="button" className="close-icon-container" onClick={ClearQuery} aria-label="Borrar búsqueda">
                        <div aria-hidden="true">
                            <Icon id="cerrar-m" height={15} color="grey7" />
                        </div>
                    </button>
                )}
                <style jsx>{styles}</style>
            </form >
            {<AutocompleteHits
                id={resultsId}
                onHitClick={handleOnSubmit}
                autocompleteFocusIndex={autocompleteFocusIndex}
                showHits={expanded}
                inputValue={inputValue}
            />}
        </>
    );
};

export default CustomSearchBox;
